import React, { useEffect, useState } from 'react';
import { Star, ChevronLeft, ChevronRight, Check, X as XIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { userimg } from '../assets/assets';
import OverlappingProfiles from './OverlappingProfiles';
import BookmarkButton from './BookmarkButton';
import Gigreview from './reviews/Gigreview';
import Ratingstar from './reviews/Ratingstar';

const GigDetails = ({ gig, galleryImages, currentImage, handleThumbnailClick, scrollMoreGigs, moreGigsRef, similarGigs, reviews = [], reviewStats }) => {
  const [filter, setFilter] = useState('All Reviews');

  const filteredReviews = reviews.filter(review => {
    if (filter === 'Positive Reviews') return review.rating >= 4;
    if (filter === 'Critical Reviews') return review.rating <= 3;
    return true;
  });

  const avgRating = gig?.avgRating || 0;
  const ratingCount = gig?.starNumber || 0;



  
  // Plan types in order
  const planTypes = ['basic', 'standard', 'advanced', 'pro', 'premium'];
  
  
  // Filter plans — only show packages that were actually filled in (have title + price)
  const availablePlans = planTypes
    .filter(type => {
      const p = gig?.pricing?.[type];
      return p && p.title && p.title.trim() !== '' && p.price > 0;
    })
    .map(type => ({ ...gig.pricing[type], type }));

  // Helper to render value
  // - boolean true → Check, boolean false → nothing
  // - 'included' → green Check icon, '' / falsy → red X icon (addon rows)
  // - any other string → plain text
  const renderValue = (val) => {
    if (typeof val === 'boolean') {
      return val ? <Check size={20} className="mx-auto text-green-600" /> : <XIcon size={20} className="mx-auto text-red-400" />;
    }
    if (val === 'included') {
      return <Check size={20} className="mx-auto text-green-600" />;
    }
    if (val === '' || val === null || val === undefined) {
      return <XIcon size={20} className="mx-auto text-red-400" />;
    }
    return <span className="text-sm text-gray-600">{val}</span>;
  };

  // Helper to render mobile value
  const renderMobileValue = (val, label) => {
    // Addon row: 'included' or '' → icon + label
    if (val === 'included' || val === '') {
      const isIncluded = val === 'included';
      return (
        <div className="flex items-center gap-1.5">
          {isIncluded
            ? <Check size={14} className="text-green-600 flex-shrink-0" />
            : <XIcon size={14} className="text-red-400 flex-shrink-0" />}
          <span className={`text-xs ${isIncluded ? 'text-gray-700' : 'text-gray-400 line-through'}`}>{label}</span>
        </div>
      );
    }
    if (typeof val === 'boolean') {
      return (
        <div className="flex items-center gap-1.5">
          {val
            ? <Check size={14} className="text-green-600 flex-shrink-0" />
            : <XIcon size={14} className="text-red-400 flex-shrink-0" />}
          <span className={`text-xs ${val ? 'text-gray-700' : 'text-gray-400'}`}>{label}</span>
        </div>
      );
    }
    return (
      <div className="flex flex-row gap-4 mb-1">
        <span className="text-[10px] text-gray-400 uppercase">{label}</span>
        <span className="text-xs text-gray-600">{val || '—'}</span>
      </div>
    );
  };

  // Standard keys to exclude from dynamic "other attributes" rows
  const standardKeys = ['_id', 'id', 'name', 'title', 'price', 'description', 'deliveryTimeframe', 'type'];

  // Find all unique extra attribute keys across all plans
  const extraAttributeKeys = Array.from(new Set(
    availablePlans.flatMap(plan => Object.keys(plan).filter(key => !standardKeys.includes(key)))
  ));

  return (
    <div className="bg-white font-['Poppins'] w-[95%] mx-auto">
      {/* Main content */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h1 className="text-[15px] md:text-2xl font-medium md:font-bold">{gig?.title}</h1>
          <BookmarkButton gigId={gig?._id} className="mt-1 flex-shrink-0" />
        </div>

        {/* Seller / Hellocians info */}
        <div className="flex items-center justify-between mb-4">

          {/* Creator details — left side */}
          {gig?.creator && (
            <div className="flex items-center gap-2">
              <img
                src={gig.creator.profilePicture || userimg}
                alt={`${gig.creator.firstName} ${gig.creator.lastName}`}
                className="md:w-9 w-7 md:h-9 h-7 rounded-full object-cover border border-gray-200 shadow-sm"
              />
              <div className="leading-tight">
                <p className="text-[10px] md:text-sm md:font-semibold font-medium text-gray-800">
                  {gig.creator.firstName} {gig.creator.lastName}
                </p>
             
                {/* Rating pill */}
                   {ratingCount > 0 && (
                     <div className="flex items-center gap-1 ">
                      
                       <Star size={10} fill="black" className="text-black" />
                       <span className="text-[10px] font-medium md:font-bold">{avgRating.toFixed(1)}</span>
                       <span className="text-[9px] text-gray-400">({ratingCount})</span>
                     </div>
                   )}

              </div>
            </div>
          )}

          <OverlappingProfiles
            hellocians={gig?.hellocians || []}
          />
        </div>


        {/* Main gallery */}
        {galleryImages && galleryImages.length > 0 && (
          <div className="mb-6">
            <div className="w-full h-[134px] md:h-[470px] bg-gray-200 rounded-md overflow-hidden mb-2 md:mb-3 relative">
              {galleryImages.map((image, index) => (
                <img
                  key={image.id}
                  src={image.main || image}
                  alt={image.alt || `Gallery image ${index + 1}`}
                  className={`w-full h-full object-cover absolute transition-opacity duration-300 ${
                    index === currentImage ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}

              {galleryImages.length > 1 && (
                <>
                  <button
                    onClick={() => handleThumbnailClick((currentImage - 1 + galleryImages.length) % galleryImages.length)}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/70 rounded-full p-1 hover:bg-white"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    onClick={() => handleThumbnailClick((currentImage + 1) % galleryImages.length)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/70 rounded-full p-1 hover:bg-white"
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnails */}
            {galleryImages.length > 1 && (
              <div className="flex space-x-2 md:space-x-3 overflow-x-auto md:grid md:grid-cols-4 md:gap-3 md:overflow-x-visible">
                {galleryImages.map((image, index) => (
                  <div
                    key={image.id}
                    className={`flex-shrink-0 w-20 h-20 md:w-full md:h-[193px] bg-gray-200 rounded-md overflow-hidden cursor-pointer border-2 transition-all ${
                      index === currentImage ? 'border-blue-500 scale-95 shadow-inner' : 'border-transparent'
                    }`}
                    onClick={() => handleThumbnailClick(index)}
                  >
                    <img src={image.thumbnail || image} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* About this gig */}
        <div className="mb-8 overflow-hidden">
          <h2 className="text-base md:text-xl font-bold mb-3 border-b pb-2">About this Gig</h2>
          <div className="text-[12px] md:text-base text-gray-600 mb-4 leading-relaxed break-words overflow-hidden w-full" dangerouslySetInnerHTML={{ __html: gig?.description || '' }}></div>

          {/* Tags */}
          {gig?.tags && gig.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-5">
              {gig.tags.map((tag) => (
                <span key={tag._id || tag} className="bg-gray-100 md:font-medium font-normal text-gray-500 md:text-xs text-[10px] px-2 py-1 md:px-4 md:py-1.5 rounded-full hover:bg-gray-200 transition-colors cursor-default">
                  #{tag.name || tag}
                </span>
              ))}
            </div>
          )}
        </div>

    

        {/* Pricing section */}
        {availablePlans.length > 0 && (
          <div className="mb-10">
            <h2 className="text-base md:text-xl md:font-bold font-semibold mb-4">
              Pricing
            </h2>

            {/* Desktop pricing table */}
            <div className="hidden md:block overflow-x-auto ring-1 ring-gray-200">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="p-4 text-left border-b border-gray-200 font-bold text-gray-500 w-1/5">Features</th>
                    {availablePlans.map((plan, index) => (
                      <th key={plan.type} className="p-4 border-b border-l border-gray-200 text-left w-1/6">
                        <div className="font-black text-2xl text-[#174568]">${plan.price}</div>
                        <div className="text-[14px] font-bold text-gray-800 uppercase tracking-tight">{plan.title}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Delivery time */}
                  <tr>
                    <td className="p-4 border-b border-gray-100 font-medium text-gray-700">Delivery Time</td>
                    {availablePlans.map((plan) => (
                      <td key={plan.type} className="p-4 border-b border-l border-gray-100 text-left text-sm font-semibold text-gray-500">
                        {plan.deliveryTimeframe}
                      </td>
                    ))}
                  </tr>
                  {/* Description */}
                  <tr>
                    <td className="p-4 border-b border-gray-100 font-medium text-gray-700">Summary</td>
                    {availablePlans.map((plan) => (
                      <td key={plan.type} className="p-4 border-b border-l border-gray-100 text-left text-xs text-gray-400 italic">
                        {plan.description}
                      </td>
                    ))}
                  </tr>
                  {/* Dynamic attributes from plan object */}
                  {extraAttributeKeys.map(key => (
                    <tr key={key}>
                      <td className="p-4 border-b border-gray-100 font-medium text-gray-700 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </td>
                      {availablePlans.map((plan) => (
                        <td key={plan.type} className="p-4 border-b border-l border-gray-100 text-center">
                          {renderValue(plan[key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {/* Addons */}
                  {gig?.addons && gig.addons.map((addon, idx) => (
                    <tr key={idx}>
                      <td className="p-4 border-b border-gray-100 font-medium text-gray-700">{addon.name}</td>
                      {availablePlans.map((plan) => (
                        <td key={plan.type} className="p-4 border-b border-l border-gray-100 text-center">
                          {renderValue(addon.values?.[plan.type])}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {/* Action buttons */}
                  <tr className="bg-white hover:bg-transparent">
                    <td className="p-4 border-none"></td>
                    {availablePlans.map((plan) => (
                      <td key={plan.type} className="p-2.5 border-none border-l border-gray-50 text-center">
                        <a 
                          href={`/payment/${gig?._id}/${plan.type}`}
                          className="bg-[#174568] block text-[14px] text-white py-1.5 px-2 rounded-lg font-medium hover:bg-black transition-all transform hover:-translate-y-0.5 w-full shadow-md text-center"
                        >
                          Continue
                        </a>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mobile pricing cards */}
            <div className="md:hidden overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide">
              <div className="inline-flex space-x-4 min-w-full">
                {availablePlans.map((plan) => (
                  <div key={plan.type} className="w-64 flex-shrink-0 border border-gray-200 overflow-hidden bg-white">
                    <div className="bg-[#174568] p-4 text-white">
                      <div className="font-semibold text-white text-2xl">${plan.price}</div>
                      <div className='text-[15px]'>{plan.type}</div> 
                      <div className="text-white/80 text-sm font-medium">{plan.title}</div>
                      <div className="text-white/60 text-[10px] mt-1 italic line-clamp-2">{plan.description}</div>
                    </div>
                    <div className="p-4 space-y-4">
                      {/* Standard Rows */}
                      {renderMobileValue(plan.deliveryTimeframe, 'Delivery')}
                      
                      {/* Extra attributes */}
                      {extraAttributeKeys.map(key => (
                        <React.Fragment key={key}>
                          {renderMobileValue(plan[key], key.replace(/([A-Z])/g, ' $1').trim())}
                        </React.Fragment>
                      ))}

                      {/* Addons */}
                      {gig?.addons && gig.addons.map((addon, idx) => (
                        <React.Fragment key={idx}>
                          {renderMobileValue(addon.values?.[plan.type], addon.name)}
                        </React.Fragment>
                      ))}

                      <Link 
                        to={`/payment/${gig?._id}/${plan.type}`}
                        className="bg-[#174568] block text-center text-white py-1.5 px-2 rounded-sm font-medium hover:bg-black transition-all w-full text-sm mt-4"
                      >
                        Continue
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Reviews Section */}
        <div className="md:mt-8 border-t border-gray-100 md:pt-5">
          <Gigreview 
            totalReviews={reviewStats?.total || 0}
            oneStar={reviewStats?.stars?.[1] || 0}
            twoStar={reviewStats?.stars?.[2] || 0}
            threeStar={reviewStats?.stars?.[3] || 0}
            fourStar={reviewStats?.stars?.[4] || 0}
            fiveStar={reviewStats?.stars?.[5] || 0}
            averageRating={reviewStats?.average || 0}
            onFilterChange={setFilter}
          />

          <div className="mt-10 space-y-4">
            {filteredReviews.length > 0 ? (
              filteredReviews.map((review) => (
                <div key={review._id} className="border-b border-gray-100 pb-8 last:border-0">
                  <div className="flex items-start gap-4 mb-3">
                    <img 
                      src={review.userId?.profilePicture || userimg} 
                      alt={review.userId?.firstName} 
                      className="w-8 h-8 md:w-12 md:h-12 rounded-full object-cover border border-gray-100"
                    />
                    <div className="flex-grow">
                      <div className="flex items-center gap-5 mb-1">
                        <h4 className="font-medium md:font-semibold text-gray-800">
                          {review.userId?.firstName} {review.userId?.lastName}
                        </h4>
                        <span className="text-xs text-gray-400">
                          {new Date(review.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <Ratingstar rating={review.rating} size={14} />
                    </div>
                  </div>
                  <p className="text-gray-600 text-[12px] md:text-sm leading-relaxed pl-12 md:pl-16">
                    {review.comment}
                  </p>
                  
                  {review.response?.comment && (
                    <div className="ml-16 mt-4 p-4 bg-gray-50 rounded-xl border-l-4 border-[#174568]">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-bold text-[#174568] uppercase tracking-wider">Seller Response</span>
                      </div>
                      <p className="text-gray-600 text-sm italic">
                        "{review.response.comment}"
                      </p>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-10 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                <p className="text-gray-400 text-sm">No reviews yet for this gig.</p>
              </div>
            )}
          </div>
        </div>

        {/* More gigs section */}
        {similarGigs && similarGigs.length > 0 && (
          <div className="relative mt-8 pt-4 border-t border-gray-100">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-base md:text-xl font-medium md:font-bold text-gray-800">Similar Gigs</h2>
              <div className="flex gap-2">
                <button
                  className="hidden md:flex items-center justify-center w-10 h-10 border border-gray-200 rounded-full hover:bg-white hover:shadow-md transition-all text-gray-500"
                  onClick={() => scrollMoreGigs('left')}
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  className="hidden md:flex items-center justify-center w-10 h-10 border border-gray-200 rounded-full hover:bg-white hover:shadow-md transition-all text-gray-500"
                  onClick={() => scrollMoreGigs('right')}
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>

            <div
              ref={moreGigsRef}
              className="w-full mx-auto flex md:grid md:grid-flow-col md:auto-cols-max gap-4 md:gap-6 overflow-x-auto scrollbar-hide px-2 py-4"
            >
              {similarGigs.map((g) => (
                <a 
                  href={`/gigs/${g.slug}`} 
                  key={g._id || g.id} 
                  className="w-64 md:w-72 bg-white border border-gray-100 rounded-sm overflow-hidden hover:shadow-md transition-all flex-shrink-0 group block"
                >
                  <div className="h-40 md:h-44 bg-gray-100 relative overflow-hidden">
                    <img src={g.cover || g.image} alt={g.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <div className="absolute top-3 right-3 bg-white/90 px-2 py-1 rounded shadow-sm text-xs font-bold text-[#174568]">
                      ${g.pricing?.basic?.price || '—'}
                    </div>
                  </div>
                  <div className="p-4 flex flex-col h-[100px] md:h-auto">
                    <h3 className="text-[10px] md:text-sm font-medium md:font-bold text-gray-800 mb-3 md:line-clamp-2 h-10 group-hover:text-[#174568] transition-colors">{g.title}</h3>
                    <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-50">
                      <div className="flex items-center">
                        <Star size={14} fill="#FFB33E" className="text-[#FFB33E] mr-1" />
                        <span className="text-sm font-semibold md:font-black text-gray-700">{g.avgRating?.toFixed(1) || '0.0'}</span>
                        <span className="text-xs text-gray-400 ml-1">({g.starNumber || 0})</span>
                      </div>
                      <ChevronRight size={18} className="text-[#174568] transform translate-x-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GigDetails;